
import { Component, Vue, Watch } from 'vue-property-decorator';
import { AgGridVue } from 'ag-grid-vue';
import { DataService } from '@/service/DataService';
import {
  CoordinatesPosition,
  LoginProvider,
  MemberRegistrationFailiureResult,
  MembersRegistration,
  Role,
  Room,
  SiteMember,
  UsersWithRights,
} from '@/utils/models';
import LoadingPopup from '../../components/popup/room/LoadingPopup.vue';
import MemberRegistrationForm from '../../components/popup/toolbar/MemberRegistrationForm.vue';
import {
  CellStyle,
  ColumnApi,
  FilterChangedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { isAdmin, isAdministrator, isSitesAdmin } from '@/utils/functions';
import { SiteInfo, User } from '../../utils/models';
import { Socket } from 'socket.io-client';
import ToolBar from '@/components/utils/tool-bar/ToolBar.vue';
import ToolBarItem from '@/components/utils/tool-bar/ToolBarItem.vue';
import { VNode } from 'vue';
import { POSITION } from 'vue-toastification';
type GridMode = 'member' | 'user' | '';

type ToolbarItemValue =
  | 'member-registration'
  | 'delete-members'
  | 'register-site-admin-role'
  | 'delete-site-admin-role'
  | 'activate-auto-delete'
  | 'deactivate-auto-delete'
  | 'download-logs';

@Component({ components: { AgGridVue, LoadingPopup, MemberRegistrationForm, ToolBar, ToolBarItem } })
export default class UserView extends Vue {
  gridMode: GridMode = '';
  usersRowData: User[] = [];
  dataService: DataService | null = null;
  rightsData: UsersWithRights | null = null;
  private lastLoadDate = 0;
  isLoading = false;
  memberAuthType = '';
  memberCandidates = '';
  selectedMembers: SiteMember[] = [];
  userGridOptions: GridOptions = {
    rowSelection: 'single',
    defaultColDef: { sortable: true, minWidth: 110, editable: false, resizable: true },
    columnDefs: [
      // { field: 'id', sortable: false },
      {
        field: 'authType',
        headerName: '認証方式',
        filter: 'agTextColumnFilter',
      },
      { field: 'upn', filter: 'agTextColumnFilter' },
      { field: 'email', filter: 'agTextColumnFilter' },
      { field: 'name', filter: 'agTextColumnFilter' },
      {
        field: 'roles',
        valueFormatter: (v) => {
          if (!v.value?.length) {
            return '-';
          }
          const roles = v.value as Role[];
          return roles.map((r) => r.name).join(',');
        },
        filter: 'agTextColumnFilter',
      },
      { field: 'company', filter: 'agTextColumnFilter' },
      { field: 'department1', filter: 'agTextColumnFilter' },
      {
        field: 'lastLogin',
        valueFormatter: (v) => (v?.value ? this.formatDate(new Date(v.value)) : '-'),
        filter: 'agTextColumnFilter',
      },
    ],
  };
  memberGridOptions: GridOptions = {
    rowSelection: 'single',
    defaultColDef: { sortable: true, minWidth: 110, editable: false, resizable: true },
    columnDefs: [
      {
        field: 'authType',
        headerName: '認証方式',
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'upn',
        filter: 'agTextColumnFilter',
        valueGetter: (v) => this.usersMap.get(v.data.userId ?? '')?.upn ?? '-',
      },
      { field: 'email', filter: 'agTextColumnFilter' },
      {
        field: 'status',
        // valueGetter: (v) => (v.data.userId && this.usersMap.has(v.data.userId) ? '' : '未ログイン'),
        valueGetter: (v) => {
          const userId = v.data.userId;
          if (!userId || !this.usersMap.has(userId)) {
            return '未ログイン';
          }
          if (this.onlineUsers.has(userId)) {
            const online = this.onlineUsers.get(userId);
            const rooms = online?.roomId.map((r) => this.roomMap.get(r)?.name)?.join(',') ?? '';
            return rooms ? `入室中(${rooms})` : 'オンライン';
          }
          return 'オフライン';
        },
        filter: 'agTextColumnFilter',
        cellStyle: (params) => {
          if (params.value === 'オフライン') {
            //mark police cells as red
            return { color: 'gray', backgroundColor: 'lightgray' } as CellStyle;
          } else if (params.value === '未ログイン') {
            return { color: 'red' } as CellStyle;
          }
          return null;
        },
      },
      {
        field: 'name',
        valueGetter: (v) => this.usersMap.get(v.data.userId ?? '')?.name ?? '-',
        filter: 'agTextColumnFilter',
      },
      {
        field: 'roles',
        valueGetter: (v) => {
          if (!v.data.userId && v.data.isSitesAdmin) {
            return 'サイト管理者';
          } else if (!v.data.userId) {
            return '-';
          }
          let roles: Role[] = this.usersMap.get(v.data.userId)?.roles || [];
          if (v.data.email && !!this.roomAdminsMap.get(v.data.email)?.length) {
            roles = [...roles, { name: 'ルーム管理者', id: 'roomadmin' }];
          }
          return roles.map((r) => r.name).join(',');
        },
        filter: 'agTextColumnFilter',
      },
      {
        field: 'autoDelete',
        headerName: '自動削除',
        valueGetter: (v) => (v.data.autoDelete ? '有効' : '無効'),
        filter: 'agTextColumnFilter',
        hide: !this.$store.getters.isTrialSite,
      },
      {
        field: 'roomAdmin',
        headerName: '管理対象ルーム名',
        valueGetter: (v) => {
          if (!v.data.email) {
            // あり得ないけど一応。
            return '-';
          }
          const rooms: Room[] = this.roomAdminsMap.get(v.data.email) ?? [];
          return rooms.map((r) => r.name).join(',');
        },
        filter: 'agTextColumnFilter',
      },
      { field: 'company', filter: 'agTextColumnFilter' },
      { field: 'department1', filter: 'agTextColumnFilter' },
      {
        field: 'lastLogin',
        valueGetter: (v) => this.usersMap.get(v.data.userId ?? '')?.lastLogin,
        valueFormatter: (v) => this.formatDateFromValue(v?.value),
        filter: 'agTextColumnFilter',
      },
    ],
  };
  private columnApi: ColumnApi | null = null;
  authLoginProviders: LoginProvider[] = [];
  private gridApi: GridApi | null = null;
  filteredCount = 0;
  siteInfo: SiteInfo = {} as any;
  memberRowData: SiteMember[] = [];
  usersMap: Map<string, User> = new Map<string, User>();
  roomAdminsMap: Map<string, Room[]> = new Map<string, Room[]>();
  onlineUsers = new Map<string, { userId: string; roomId: string[] }>();
  roomMap: Map<string, Room> = new Map<string, Room>();
  socket: Socket | null = null;
  selectedToolbarItem = '';

  popupPosition: CoordinatesPosition = { x: 0, y: 0 };

  get user(): User {
    return this.$store.state.user;
  }
  get enableProviders() {
    return this.authLoginProviders.filter((v) => v.enable);
  }
  get canRegieterProviders() {
    console.log(this.enableProviders, this.$store.state.siteInfo?.authLoginProviders);
    return this.enableProviders.filter(
      (v) => v.canRegister && this.$store.state.siteInfo?.authLoginProviders?.includes(v.id),
    );
  }
  get canRegisterMember(): boolean {
    return !!(this.memberAuthType && this.memberCandidates.trim());
  }
  get canDeleteMember(): boolean {
    return this.selectedMembers.length > 0;
  }
  get canActivateAutoDelete(): boolean {
    return this.selectedMembers.length > 0;
  }
  get isAdmin(): boolean {
    return isAdmin(this.user);
  }
  get isAdministrator(): boolean {
    return isAdministrator(this.user);
  }
  get isSitesAdmin(): boolean {
    return isSitesAdmin(this.user);
  }

  get isOpenToolBarPopup() {
    return this.selectedToolbarItem === 'member-registration';
  }

  @Watch('$route.fullPath')
  onQueryChange() {
    this.gridMode = this.$route.query.mode as GridMode;
  }

  mounted() {
    this.dataService = this.$store.state.dataService;

    this.dataService!.emitGetLoginProviders().then((pv) => {
      this.authLoginProviders = pv;
      pv.sort((a, b) => a.sortOrder - b.sortOrder);
    });
    this.socket = this.dataService!.onMemberRegistrationFailed(this.onMemberRegistrationFailed);
    this.siteInfo = this.$store.state.siteInfo as SiteInfo;
    this.gridMode = this.$route.query.mode as GridMode;
    if (!this.rightsData || this.lastLoadDate + 1 * 60 * 1000 < Date.now()) {
      this.lastLoadDate = Date.now();
      this.loadUserMemberData();
    }
    window.addEventListener('resize', this.onRepositionPopup);
    window.addEventListener('scroll', this.onRepositionPopup);
  }

  beforeDestroy() {
    this.socket?.removeListener('member registration failed', this.onMemberRegistrationFailed);
    window.removeEventListener('resize', this.onRepositionPopup);
    window.removeEventListener('scroll', this.onRepositionPopup);
  }

  onMemberRegistrationFailed = (members: MemberRegistrationFailiureResult[]) => {
    this.$toast.error(
      `以下のメンバーの登録が失敗しました。\n
    ${members.map((m) => `${m.email}: ${m.reason}`).join(', \n\n')}
    `,
      {
        timeout: 0,
        position: POSITION.BOTTOM_CENTER,
      },
    );
  };

  formatDateFromValue(date: any) {
    if (!date) {
      return '-';
    }
    if (typeof date === 'string') {
      return date;
    }
    if (typeof date === 'number') {
      return this.formatDate(new Date(date));
    }
    if (date instanceof Date) {
      return this.formatDate(date);
    }
    return '-';
  }

  formatDate(date: Date) {
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  showUser() {
    if (this.lastLoadDate + 1 * 60 * 1000 < Date.now()) {
      this.lastLoadDate = Date.now();
      this.loadUserMemberData();
    }
  }
  showMember() {
    if (this.lastLoadDate + 1 * 60 * 1000 < Date.now()) {
      this.lastLoadDate = Date.now();
      this.loadUserMemberData();
    }
  }
  addMember(membersRegistration: MembersRegistration) {
    this.dataService!.emitRegisterMembers(membersRegistration).then((res) => {
      if (membersRegistration.createZawaroomAccount && res.length === membersRegistration.members.length)
        this.$toast.success('アカウント作成に成功しました');
      this.gridApi!.applyTransaction({ add: res });
    });
    this.memberCandidates = '';
  }
  deleteMembers() {
    if (!this.canDeleteMember) {
      return;
    }
    const remove = this.selectedMembers;
    this.dataService!.emitRemoveMembers(remove);
    this.gridApi!.applyTransaction({ remove });
  }
  registerSiteAdminRole() {
    if (!this.canDeleteMember) {
      return;
    }
    const members = this.selectedMembers;
    this.dataService!.emitRegisterSiteAdeminRole(members).then((users) => {
      members.forEach((m) => (m.isSitesAdmin = true));
      users.forEach((u) => {
        const m = this.usersMap.get(u.id);
        if (m) {
          m.roles = u.roles;
        }
      });
      this.gridApi?.applyTransaction({ update: members });
    });
  }
  deleteSiteAdminRole() {
    if (!this.canDeleteMember) {
      return;
    }
    const members = this.selectedMembers;
    this.dataService!.emitRemoveSiteAdeminRole(members).then((users) => {
      members.forEach((m) => (m.isSitesAdmin = false));
      users.forEach((u) => {
        const m = this.usersMap.get(u.id);
        if (m) {
          m.roles = u.roles;
        }
      });
      this.gridApi?.applyTransaction({ update: members });
    });
  }
  async activateAutoDelete() {
    if (!this.canActivateAutoDelete) return;
    await this.dataService!.emitActivateAutoDelete(this.selectedMembers);
    for (const m of this.selectedMembers) {
      m.autoDelete = true;
    }
    console.log(this.selectedMembers);
    this.gridApi?.applyTransaction({ update: this.selectedMembers });
  }
  async deactivateAutoDelete() {
    if (!this.canActivateAutoDelete) return;
    await this.dataService!.emitDeactivateAutoDelete(this.selectedMembers);
    for (const m of this.selectedMembers) {
      m.autoDelete = false;
    }
    this.gridApi?.applyTransaction({ update: this.selectedMembers });
  }
  private loadUserMemberData() {
    this.isLoading = true;
    return this.dataService!.emitGetUsers().then((res) => {
      this.isLoading = false;
      this.usersRowData = res.users ?? [];
      this.memberRowData = res.siteMembers ?? [];
      console.log(this.memberRowData);
      this.filteredCount = this.memberRowData.length;
      this.rightsData = res;
      this.usersMap.clear();
      res.users.forEach((u) => this.usersMap.set(u.id, u));
      this.roomAdminsMap.clear();
      res.rooms.forEach((r) => {
        this.roomMap.set(r.id, r);
        if (!r.adminEmails || r.adminEmails.length === 0) {
          return;
        }
        r.adminEmails.forEach((email) => {
          if (!this.roomAdminsMap.has(email)) {
            this.roomAdminsMap.set(email, []);
          }
          const list = this.roomAdminsMap.get(email) as Room[];
          list.push(r);
        });
      });
      this.onlineUsers.clear();
      res.loginUserIds.forEach((u) => {
        if (!this.onlineUsers.has(u)) {
          this.onlineUsers.set(u, { userId: u, roomId: [] });
        }
      });
      res.roomStates.forEach((s) => {
        if (s.userId && this.onlineUsers.has(s.userId)) {
          const t = this.onlineUsers.get(s.userId);
          t!.roomId.push(s.roomId);
        } else if (s.userId) {
          // おかしい？
          console.log('no online user but in room', s);
        }
      });
    });
  }
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
  }
  onSelectionChanged(params: SelectionChangedEvent) {
    this.selectedMembers = params.api.getSelectedRows();
  }
  onFilterChanged(ev: FilterChangedEvent) {
    this.filteredCount = ev.api.getDisplayedRowCount();
  }
  downloadLogsByCsv() {
    this.gridApi?.exportDataAsCsv({
      processCellCallback: (v) =>
        v.column.getColDef().field === 'lastLogin' ? this.formatDateFromValue(v.value) : v.value,
    });
  }

  onSelectToolbarItem(value: ToolbarItemValue) {
    switch (value) {
      case 'member-registration':
        break;
      case 'delete-members':
        this.deleteMembers();
        break;
      case 'register-site-admin-role':
        this.registerSiteAdminRole();
        break;
      case 'delete-site-admin-role':
        this.deleteSiteAdminRole();
        break;
      case 'activate-auto-delete':
        this.activateAutoDelete();
        break;
      case 'deactivate-auto-delete':
        this.deactivateAutoDelete();
        break;
      case 'download-logs':
        this.downloadLogsByCsv();
        break;
    }
    this.selectedToolbarItem = this.selectedToolbarItem === value ? '' : value;
    this.onRepositionPopup();
  }

  onRepositionPopup() {
    const toolbarRef = this.$refs.toolbar as ToolBar | undefined;
    const toolbarItemRef = this.$refs[this.selectedToolbarItem] as ToolBarItem | undefined;
    if (toolbarRef && toolbarItemRef) {
      this.popupPosition = toolbarRef.calculatePopupPositoin(toolbarItemRef);
    }
  }
}
